import { Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'progress-bar-countdown',
    templateUrl: 'progress-bar-countdown.component.html',
    styleUrls: ['progress-bar-countdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProgressBarCountdownComponent implements OnDestroy {
  @Input() shouldAnnounceTime: boolean;
  @Input() ariaProgressLabel: string;
  @Input() ariaExtendButtonLabel: string;

  @Input({ required: true }) countdown$: Observable<number>;
  @Input({ required: true }) totalSeconds: number; // add this input so the component knows the full time

  @Output() extend = new EventEmitter<void>();

  protected showExtendButton: boolean = false;
  protected shouldAnnounce: boolean = false;
  ONE_MINUTE = 60;
  protected timerAnnouncement: string;

  private _destroy = new Subject<void>();

  constructor(private readonly languageService: LanguageService) {}

  onExtend() {
    this.extend.emit();
  }

  getClampedProgress(secondsLeft: number): number {
    const lowLimit = 30;
    const progressTotal = secondsLeft <= lowLimit ? Math.min(lowLimit, this.totalSeconds) : this.totalSeconds;
    const progress = (secondsLeft / progressTotal) * 100;

    // Clamp between 0 and 100
    return Math.min(Math.max(progress, 0), 100);
  }

  formatTime(seconds: number): string {
    const exactMinutes = seconds / this.ONE_MINUTE;
    const remainingSeconds = Math.floor(seconds % this.ONE_MINUTE);

    // Special case: if we're at exactly 1 minute (60 seconds) or less, show seconds countdown
    if (seconds <= this.ONE_MINUTE) {
      if (seconds <= 30) {
        return `${Math.ceil(seconds)} ${
          Math.ceil(seconds) === 1
            ? this.languageService.instant('COMMON.SECOND_LEFT')
            : this.languageService.instant('COMMON.SECONDS_LEFT')
        }`;
      }
      return `${Math.ceil(seconds / 10) * 10} ${this.languageService.instant('COMMON.SECONDS_LEFT')} `;
    }

    // For more than 1 minute:
    // Round up to the next minute if there are any seconds remaining
    let displayMinutes = Math.floor(exactMinutes);
    if (remainingSeconds > 0) {
      displayMinutes += 1;
    }

    // Show minutes
    return `${displayMinutes} ${this.languageService.instant('COMMON.MINUTES_LEFT')}`;
  }

  ngOnDestroy(): void {
    this._destroy.next();
  }
}
